import { ColocaProgress, type ColocaProgressSegment } from "@/shared/components/feedback/ColocaProgress";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { DETAIL_POLL_MS } from "../../constants/payoutBatches.constants";
import type { PayoutBatch } from "../../types/payoutBatches.types";
import { executionProgress } from "../../utils/batchStatus";

export interface BatchProgressPanelProps {
  batch: PayoutBatch;
  lastUpdatedAt: Date | null;
  isLive: boolean;
  /** True when the socket is delivering pushes; the caption says "live" instead of the poll cadence. */
  isSocketConnected: boolean;
}

const LEGEND_TONE: Record<string, string> = {
  paid: "bg-success",
  failed: "bg-destructive",
  submitted: "bg-warning",
  processing: "bg-primary",
  pending: "bg-muted",
};

/**
 * The batch strip in full: paid / failed / awaiting settlement / processing over the chosen rows, with a
 * legend.
 */
export function BatchProgressPanel({
  batch,
  lastUpdatedAt,
  isLive,
  isSocketConnected,
}: BatchProgressPanelProps) {
  const { t, activeLanguage } = useTranslations("payoutBatches");
  const progress = executionProgress(batch.counters);
  const pending = Math.max(
    0,
    progress.max - progress.segments.reduce((sum, segment) => sum + segment.value, 0),
  );

  const legendLabel = (key: string): string => {
    switch (key) {
      case "paid":
        return t("progress_legend_paid");
      case "failed":
        return t("progress_legend_failed");
      case "submitted":
        return t("progress_legend_submitted");
      case "processing":
        return t("progress_legend_processing");
      default:
        return t("progress_legend_pending");
    }
  };

  const segments: ColocaProgressSegment[] = progress.segments.map((segment) => ({
    ...segment,
    label: legendLabel(segment.key),
  }));

  return (
    <ColocaSurface padding="lg" className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground">{t("progress_title")}</h2>
        {lastUpdatedAt && (
          <p className="font-mono text-[11px] text-muted-foreground tabular-nums" aria-live="polite">
            {t("detail_updated_at", {
              time: lastUpdatedAt.toLocaleTimeString(activeLanguage, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              }),
            })}
            {isLive
              ? ` · ${isSocketConnected ? t("detail_live") : t("detail_polling", { seconds: DETAIL_POLL_MS / 1000 })}`
              : ""}
          </p>
        )}
      </div>
      <ColocaProgress
        value={progress.done}
        max={progress.max}
        size="lg"
        segments={segments}
        label={t("progress_processed", {
          done: progress.done,
          total: progress.max,
        })}
        showValue
      />
      <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
        {[
          ...segments,
          {
            key: "pending",
            value: pending,
            tone: "neutral" as const,
            label: t("progress_legend_pending"),
          },
        ].map((segment) => (
          <li key={segment.key} className="flex items-center gap-2">
            <span aria-hidden="true" className={cn("size-2 rounded-full", LEGEND_TONE[segment.key])} />
            <span>{segment.label}</span>
            <span className="font-mono text-foreground tabular-nums">{segment.value}</span>
          </li>
        ))}
      </ul>
    </ColocaSurface>
  );
}
